import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatError } from '@angular/material/form-field';
import { TranslatePipe } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
/**
 * Provides a UI to manage the count of the quantity, typically by using
 * increase and decrease functinality. The item counter expects an input `FormControl`
 * so that the state of the control can be managed outside of this component.
 */
@Component({
  selector: 'cx-item-counter',
  templateUrl: './item-counter.component.html',
  imports: [ReactiveFormsModule, MatError, AsyncPipe, TranslatePipe],
})
export class ItemCounterComponent implements OnInit, OnDestroy {
  /**
   * Holds the value of the counter, the state of the `FormControl`
   * can be managed outside of the item counter.
   */
  @Input() control: FormControl;
  resetPDPQtySubscription$: Subscription;
  resetMOQAddCartSubscription$: Subscription;

  /**
   * This can be used in case an item has a minmum order quantity.
   * @default 1
   */
  @Input() min = 1;

  /**
   * This can be used in case an item has a maximum order quantity.
   */
  @Input() max: number;

  /**
   * The step is used to increment the count. It is supposed to be a
   * positive inteteger or float.
   * @default 1
   */
  @Input() step = 1;

  /**
   * Inidicates that the input can be manually set to zero,
   * despite the fact that the input controls will be limited to
   * the minimum. The zero value can be used to remove an item.
   */
  @Input() allowZero = false;

  @Input() itemCode: any;

  @Input() moq = 1;
  @Input() isMinQty = true;

  private _control$: Observable<FormControl>;
  isFocused = false;
  value: string;
  initialValue: string = '';
  /**
   * In readonly mode the item counter will only be shown as a label,
   * the form controls are not rendered.
   * Please not that readonly is different from the `disabled` form state.
   * @default false
   */
  @HostBinding('class.readonly') @Input() readonly = false;

  @ViewChild('qty', { static: false }) private input: ElementRef<HTMLInputElement>;
  @Output()
  update = new EventEmitter<number>();
  @Output() hasMinQtyError = new EventEmitter<boolean>();

  @HostListener('click') handleClick() {
    this.input.nativeElement.focus();
  }

  @HostListener('change') handleChange() {
    this.componentService.setDealerPriceSpinner(this.itemCode);
    const newValue = ShimanoUtilMethod.setMinimumOrderQuantity(this.input.nativeElement.value, this.moq);
    const checkQty = ShimanoUtilMethod.checkUserQuantity(this.input.nativeElement.value, this.moq);
    this.control.setValue(newValue);
    if (checkQty > 0) {
      this.control.setErrors({ val: true });
      this.isMinQty = false;
      ShimanoUtilMethod.appendToStorage('MOQCartError', this.itemCode);
    } else {
      this.control.setErrors(null);
      this.isMinQty = true;
      ShimanoUtilMethod.removeToStorage('MOQCartError', this.itemCode);
    }
    this.hasMinQtyError.emit(this.isMinQty);
    this.control.markAsDirty();
    if (!this.control.valid) {
      this.control.updateValueAndValidity();
    }
    this.componentService.isItemCounterChanged$.next(true);
  }

  constructor(private componentService: ComponentService, private cd: ChangeDetectorRef) {}

  increment() {
    // it's too early to use the `stepUp` and `stepDown` API...
    // let's wait for FF: https://caniuse.com/#search=stepUp
    this.control.setValue(this.control.value + this.step);
    this.control.markAsDirty();
  }

  decrement() {
    this.control.setValue(this.control.value - this.step);
    this.control.markAsDirty();
  }

  /**
   * Returns an observable with the control. The value changes of the
   * control are intercepted in order to suppress invalid values.
   */
  getControl(): Observable<FormControl> {
    if (!this._control$) {
      this._control$ = this.control.valueChanges.pipe(
        startWith(this.control.value),
        tap((value) => {
          this.update.emit(value);
          this.control.setValue(this.getValidCount(value), { emitEvent: false });
        }),
        map(() => this.control)
      );
    }
    return this._control$;
  }

  /**
   * Validate that the given value is in between
   * the `min` and `max` value. If the value is out
   * of  the min/max range, it will be altered.
   * If `allowZero` is set to true, the 0 value is ignored.
   *
   */
  private getValidCount(value: number) {
    if (value < this.min && !(value === 0 && this.allowZero)) {
      value = this.min;
    }
    if (this.max && value > this.max) {
      value = this.max;
    }
    return value;
  }

  ngOnInit() {
    this.resetMOQAddCartSubscription$ = this.componentService.resetMOQAddCart$.subscribe((reset) => {
      this.isMinQty = reset;
      this.cd.detectChanges();
    });

    this.resetPDPQtySubscription$ = this.componentService.resetPdpQty$.subscribe((data) => {
      this.input.nativeElement.value = data;
    });
  }

  onFocus(event: any) {
    this.isFocused = event ? true : false;
  }

  handleInputChanged(event: any) {
    this.value = event.target.value;
    this.value = this.value.replace(/^[0]+/g, '').slice(0, 3);

    if (this.value.length === 3 && !this.isFocused) {
      this.update.emit(+this.value);
      this.componentService.setDealerPriceSpinner(this.itemCode);
    }
    event.target.value = this.value;
  }
  onBlur(event: any) {
    this.isFocused = false;
    let finalValue = this.value.trim() === '' || undefined ? '1' : this.value;
    const newValue = ShimanoUtilMethod.setMinimumOrderQuantity(finalValue, this.moq);
    const checkQty = ShimanoUtilMethod.checkUserQuantity(finalValue, this.moq);
    this.control.setValue(newValue);
    this.update.emit(newValue);

    this.control.setValue(newValue);
    this.update.emit(newValue);
    this.componentService.setDealerPriceSpinner(this.itemCode);
    if (checkQty > 0) {
      this.control.setErrors({ val: true });
      this.isMinQty = false;
      ShimanoUtilMethod.appendToStorage('MOQCartError', this.itemCode);
    } else {
      this.control.setErrors(null);
      this.isMinQty = true;
      ShimanoUtilMethod.removeToStorage('MOQCartError', this.itemCode);
    }
    this.hasMinQtyError.emit(this.isMinQty);
    this.control.markAsDirty();
    if (!this.control.valid) {
      this.control.updateValueAndValidity();
    }
  }

  ngOnDestroy() {
    if (this.resetPDPQtySubscription$) {
      this.resetPDPQtySubscription$.unsubscribe();
    }
    if (this.resetMOQAddCartSubscription$) {
      this.resetMOQAddCartSubscription$.unsubscribe();
    }
  }
}
